import { useEffect, useState } from 'react';

const formattedGrade = (grade: number) => {
  switch (grade) {
    case 0:
      return 'K';
    case 1:
      return '1st';
    case 2:
      return '2nd';
    case 3:
      return '3rd';
    default:
      return grade + 'th';
  }
};

/**
 * Custom event dispatched whenever the homepage grade localStorage entry is
 * written from anywhere (home selector dropdown, in-form grade pickers, etc.).
 * The home selector listens for this so it stays in sync with changes made
 * inside Level forms.
 */
export const HOME_GRADE_CHANGED_EVENT = 'curipod:home-grade-changed';

/**
 * Reads the bare grade value (e.g. "6", "K") from the homepage localStorage key.
 * Returns undefined when nothing is stored.
 */
export const getHomeGrade = (): string | undefined => {
  try {
    const data = localStorage.getItem('@curipod_v1_home_grade');
    if (data) {
      const { value } = JSON.parse(data) as { value: string };
      return value || undefined;
    }
  } catch {
    /* ignore */
  }
  return undefined;
};

/**
 * Reactive version of getHomeGrade: re-reads localStorage whenever the
 * HOME_GRADE_CHANGED_EVENT fires so components built around the home grade
 * (e.g. grid card badges) re-render after an in-form grade change.
 */
export const useHomeGrade = (): string | undefined => {
  const [grade, setGrade] = useState<string | undefined>(() => getHomeGrade());
  useEffect(() => {
    const handler = (e: Event) => {
      const next = (e as CustomEvent<string>).detail;
      setGrade(next || getHomeGrade());
    };
    window.addEventListener(HOME_GRADE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(HOME_GRADE_CHANGED_EVENT, handler);
  }, []);
  return grade;
};

/**
 * If `selectedGrade` is provided and is within `grades`, returns just that grade.
 * Otherwise returns the grouped range display (e.g. "3rd-12th").
 *
 * @param grades       - generator grade numbers as strings ("0" = K, "1", "2", …)
 * @param selectedGrade - bare homepage grade value ("K", "1", "6", …), optional
 */
export const groupedGrades = (grades: string[], selectedGrade?: string) => {
  if (selectedGrade) {
    const normalized = selectedGrade === 'K' ? '0' : selectedGrade;
    if (grades.includes(normalized)) {
      const grade = formattedGrade(parseInt(normalized));
      return <span>{grade === 'K' ? 'Kindergarten' : `${grade} Grade`}</span>;
    }
  }

  const sortedGrades = grades.sort((a, b) => parseInt(a) - parseInt(b));
  const groups: string[] = [];
  let start = 0;
  let end = 0;

  for (let i = 0; i <= sortedGrades.length; i++) {
    if (i === 0) {
      start = parseInt(sortedGrades[i]);
      end = start;
      continue;
    }
    if (parseInt(sortedGrades[i]) - end === 1) {
      end = parseInt(sortedGrades[i]);
    } else {
      if (start === end) {
        groups.push(formattedGrade(start));
      } else {
        groups.push(`${formattedGrade(start)}-${formattedGrade(end)}`);
      }
      start = parseInt(sortedGrades[i]);
      end = start;
    }
  }

  if (groups.length === 0) {
    return null;
  }

  return (
    <>
      {groups.map(grade => (
        <span key={grade}>{grade}</span>
      ))}
    </>
  );
};

const GRADES_PRE_K_TO_12 = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
];

/**
 * Convenience wrapper for a common hardcoded grade range.
 * Shows the selected grade when it falls within the range, otherwise the full range.
 */
export const gradeRangePreKto12 = (selectedGrade?: string) =>
  groupedGrades(GRADES_PRE_K_TO_12, selectedGrade);
